import { useState, useEffect } from 'react';
import { Mic, MicOff, Volume2, MessageCircle, Loader } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import VoiceRecorder from '../components/VoiceRecorder';

interface User {
  id: string;
  email?: string;
  user_metadata?: any;
}

interface VoiceQueryProps {
  user: User | null;
  onSignIn?: () => void;
}

export default function VoiceQuery({ user, onSignIn }: VoiceQueryProps) {
  const { t } = useLanguage();
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [response, setResponse] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleVoiceResult = (text: string) => {
    setTranscript(text);
    processVoiceQuery(text);
  };

  const processVoiceQuery = async (query: string) => {
    setIsProcessing(true);
    try {
      // Simulate AI processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Generate contextual response based on query
      let aiResponse = '';
      const lowerQuery = query.toLowerCase();
      
      if (lowerQuery.includes('pest') || lowerQuery.includes('bug') || lowerQuery.includes('insect')) {
        aiResponse = 'I can help you identify and control pests in your crops. Common signs include holes in leaves, discoloration, or visible insects. Would you like specific treatment recommendations?';
      } else if (lowerQuery.includes('weather') || lowerQuery.includes('rain') || lowerQuery.includes('temperature')) {
        aiResponse = 'Current weather conditions show clear skies with 23°C temperature. Perfect conditions for most farming activities. Would you like a detailed weather forecast?';
      } else if (lowerQuery.includes('crop') || lowerQuery.includes('plant') || lowerQuery.includes('grow')) {
        aiResponse = 'I can analyze your crop health and provide growing recommendations. Consider factors like soil moisture, sunlight, and nutrient levels for optimal growth.';
      } else {
        aiResponse = `I heard: "${query}". I can help with crop analysis, pest control, weather information, and farming advice. What specific farming question do you have?`;
      }
      
      setResponse(aiResponse);
    } catch (error) {
      setResponse('Sorry, I had trouble processing your voice query. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAIChat = () => {
    // Navigate to AI chat with voice query context
    const aiPanel = document.querySelector('[data-ai-panel]');
    if (aiPanel) {
      aiPanel.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => {
        const textarea = document.querySelector('textarea[placeholder*="AgroSense AI"]') as HTMLTextAreaElement;
        if (textarea) {
          textarea.value = transcript || 'I have a farming question...';
          textarea.focus();
        }
      }, 500);
    }
  };

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Mic className="w-16 h-16 text-purple-600 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Voice Query Assistant</h2>
        <p className="text-gray-600 mb-6 max-w-md">
          Sign in to use voice commands for crop analysis, pest identification, weather updates, and farming advice.
        </p>
        <button
          onClick={onSignIn}
          className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          Sign In to Continue
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <Mic className="w-8 h-8 text-purple-600" />
          Voice Query Assistant
        </h1>
        <p className="text-gray-600">
          Ask farming questions using your voice and get instant AI-powered answers
        </p>
      </div>

      {/* Voice Recording Interface */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="text-center">
          <div className="mb-6">
            <VoiceRecorder
              onResult={handleVoiceResult}
              onRecordingChange={setIsRecording}
            />
          </div>

          <div className="space-y-4">
            <div className={`p-4 rounded-lg border-2 border-dashed transition-colors ${
              isRecording 
                ? 'border-purple-400 bg-purple-50' 
                : 'border-gray-300 bg-gray-50'
            }`}>
              {isRecording ? (
                <div className="flex items-center justify-center gap-2 text-purple-600">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="font-medium">Listening...</span>
                </div>
              ) : (
                <p className="text-gray-500">
                  Click the microphone to start voice recording
                </p>
              )}
            </div>

            {transcript && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                  <Volume2 className="w-4 h-4" />
                  What you said:
                </h3>
                <p className="text-blue-700">"{transcript}"</p>
              </div>
            )}

            {isProcessing && (
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <div className="flex items-center justify-center gap-2 text-yellow-700">
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Processing your query...</span>
                </div>
              </div>
            )}

            {response && !isProcessing && (
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  AgroSense AI Response:
                </h3>
                <p className="text-green-700">{response}</p>
                <button
                  onClick={handleAIChat}
                  className="mt-3 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm"
                >
                  Continue in AI Chat
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Voice Commands Guide */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-xl font-semibold mb-4">Voice Commands You Can Try</h3>
        
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-3">
            <h4 className="font-medium text-purple-700">Crop Analysis</h4>
            <ul className="space-y-1 text-sm text-gray-600">
              <li>• "Analyze my crop health"</li>
              <li>• "What's wrong with my plants?"</li>
              <li>• "How to improve crop yield?"</li>
            </ul>
          </div>

          <div className="space-y-3">
            <h4 className="font-medium text-purple-700">Pest Control</h4>
            <ul className="space-y-1 text-sm text-gray-600">
              <li>• "Help me identify pests"</li>
              <li>• "How to control aphids?"</li>
              <li>• "Organic pest treatment options"</li>
            </ul>
          </div>

          <div className="space-y-3">
            <h4 className="font-medium text-purple-700">Weather Information</h4>
            <ul className="space-y-1 text-sm text-gray-600">
              <li>• "What's the weather forecast?"</li>
              <li>• "Will it rain tomorrow?"</li>
              <li>• "Best time to plant crops?"</li>
            </ul>
          </div>

          <div className="space-y-3">
            <h4 className="font-medium text-purple-700">General Farming</h4>
            <ul className="space-y-1 text-sm text-gray-600">
              <li>• "When should I harvest?"</li>
              <li>• "Soil preparation tips"</li>
              <li>• "Fertilizer recommendations"</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}